import { useState } from "react";
import { Eye, Filter, ListChecks, Newspaper } from "lucide-react";
import { MmcFrame } from "../../admin-framework/MmcFrame.js";
import type { SimulatorAppSurfaceProps } from "../AppSurface.js";

const eventRows = [
  { id: "4624", logName: "Security", level: "Information", source: "Security-Auditing", message: "Successful account logon" },
  { id: "4625", logName: "Security", level: "Warning", source: "Security-Auditing", message: "Failed account logon" },
  { id: "7036", logName: "System", level: "Information", source: "Service Control", message: "Service changed state" },
  { id: "1001", logName: "Application", level: "Information", source: "Application Monitor", message: "Application telemetry event" }
];

export function EventViewerApp({ activeMachine, onRunCommand }: SimulatorAppSurfaceProps) {
  const [logName, setLogName] = useState("Security");
  const [eventId, setEventId] = useState("4624");
  const [source, setSource] = useState("Security-Auditing");
  const [viewName, setViewName] = useState("Security logon successes");

  async function filterLog(next = { eventId, logName, source }) {
    await onRunCommand({
      appId: "eventvwr",
      commandType: "EVENT_VIEWER_FILTER",
      capability: "audit.event.read",
      label: `Filter ${next.logName}`,
      payload: { eventId: Number(next.eventId), eventLevel: "Information", logName: next.logName, machineId: activeMachine, source: next.source }
    });
  }

  async function recordCheck(next = { eventId, logName, source }) {
    await onRunCommand({
      appId: "eventvwr",
      commandType: "EVENT_VIEWER_RECORD_CHECK",
      capability: "audit.event.check",
      label: `Check ${next.eventId}`,
      payload: { eventId: Number(next.eventId), logName: next.logName, machineId: activeMachine, source: next.source }
    });
  }

  async function recordEvidence(next = { eventId, logName, source }) {
    await onRunCommand({
      appId: "eventvwr",
      commandType: "AUDIT_RECORD_EVENT",
      capability: "audit.event.write",
      label: `Record ${next.eventId}`,
      payload: {
        auditPolicy: { logon: "success" },
        category: next.logName,
        eventId: Number(next.eventId),
        level: "Information",
        logName: next.logName,
        machineId: activeMachine,
        message: `${next.logName} event ${next.eventId} recorded from ${next.source}.`,
        source: next.source
      }
    });
  }

  async function createView(next = { eventId, logName, source, viewName }) {
    await onRunCommand({
      appId: "eventvwr",
      commandType: "EVENT_VIEWER_CREATE_VIEW",
      capability: "audit.view.write",
      label: next.viewName,
      payload: { filter: { eventId: Number(next.eventId), logName: next.logName, source: next.source }, machineId: activeMachine, viewName: next.viewName }
    });
  }

  return (
    <div className="fd-sim-app fd-sim-mmc">
      <MmcFrame
        actions={[
          { label: "Filter", onClick: () => void filterLog() },
          { label: "Record", onClick: () => void recordEvidence() },
          { label: "Custom View", onClick: () => void createView() }
        ]}
        columns={[
          { key: "level", label: "Level" },
          { key: "time", label: "Date and Time" },
          { key: "source", label: "Source" },
          { key: "eventId", label: "Event ID" },
          { key: "category", label: "Task Category" }
        ]}
        rows={eventRows.map((row) => ({
          id: row.id,
          onDoubleClick: () => {
            setLogName(row.logName);
            setEventId(row.id);
            setSource(row.source);
            void recordCheck({ eventId: row.id, logName: row.logName, source: row.source });
          },
          values: {
            category: row.message,
            eventId: row.id,
            level: row.level,
            source: row.source,
            time: "Today 10:24"
          }
        }))}
        title="Event Viewer"
        tree={[
          { active: logName === "Security", icon: <ListChecks size={15} />, id: "security", label: "Security" },
          { active: logName === "System", icon: <Newspaper size={15} />, id: "system", label: "System" },
          { active: logName === "Application", icon: <Newspaper size={15} />, id: "application", label: "Application" },
          { icon: <Eye size={15} />, id: "custom", label: "Custom Views" }
        ]}
      >
        <section className="fd-mmc-detail-panel">
          <header>
            <div>
              <p>Log filter</p>
              <h3>{logName}</h3>
            </div>
            <button type="button" onClick={() => void recordCheck()}><Filter size={14} /> Check selection</button>
          </header>
          <div className="fd-command-form">
            <label>
              Log
              <select value={logName} onChange={(event) => setLogName(event.target.value)}>
                <option>Security</option>
                <option>System</option>
                <option>Application</option>
              </select>
            </label>
            <label>
              Event ID
              <input value={eventId} onChange={(event) => setEventId(event.target.value)} />
            </label>
            <label>
              Source
              <input value={source} onChange={(event) => setSource(event.target.value)} />
            </label>
            <label>
              View name
              <input value={viewName} onChange={(event) => setViewName(event.target.value)} />
            </label>
            <button type="button" onClick={() => void filterLog()}>Apply filter</button>
            <button type="button" onClick={() => void createView()}>Create view</button>
          </div>
        </section>
      </MmcFrame>
    </div>
  );
}
